import { timeToMinutes } from "./agenda-time";

type HasTime = {
  startTime: string;
  endTime: string;
};

export type LaidOut<T> = T & {
  column: number;
  totalColumns: number;
  /** Quantas colunas o card ocupa a partir de `column`. 1 = largura normal,
   *  >1 = card se expandiu sobre colunas vizinhas vazias no intervalo dele. */
  colSpan: number;
};

/**
 * Distribui appointments em colunas side-by-side quando sobrepõem.
 *
 * Algoritmo em 3 fases (adaptado do Google Calendar):
 *   1. Cluster transitivo — se A↔B e B↔C, A/B/C ficam no mesmo cluster.
 *      Todos compartilham o mesmo totalColumns (garante zero sobreposição
 *      visual, mesmo em combinações complexas de overlap).
 *   2. Greedy column assignment dentro do cluster (primeira coluna livre).
 *   3. COL-SPAN — pra cada card, tenta expandir pra colunas à direita
 *      que estão VAZIAS dentro do intervalo temporal dele. Isso faz com
 *      que um card sem overlap direto no próprio bloco ocupe o espaço
 *      livre ao lado, mesmo estando num cluster maior. Resultado:
 *      - Card solitário no próprio intervalo → expande (ocupa 100%).
 *      - Card com qualquer outro tocando o bloco → fica na largura mínima
 *        (1/N do cluster), sem colidir visualmente com os vizinhos.
 */
export function layoutAppointments<T extends HasTime>(items: T[]): LaidOut<T>[] {
  if (items.length === 0) return [];

  const sorted = [...items].sort((a, b) => {
    const sa = timeToMinutes(a.startTime);
    const sb = timeToMinutes(b.startTime);
    if (sa !== sb) return sa - sb;
    return timeToMinutes(a.endTime) - timeToMinutes(b.endTime);
  });

  type Placed = { item: T; column: number; startMin: number; endMin: number };

  const clusters: Placed[][] = [];
  let current: Placed[] = [];
  let clusterEnd = -1;

  for (const item of sorted) {
    const startMin = timeToMinutes(item.startTime);
    const endMin = timeToMinutes(item.endTime);

    // Quebra cluster quando não há mais overlap transitivo
    if (current.length > 0 && startMin >= clusterEnd) {
      clusters.push(current);
      current = [];
      clusterEnd = -1;
    }

    // Primeira coluna livre dentro do cluster atual
    const used = new Set<number>();
    for (const p of current) {
      if (p.endMin > startMin) used.add(p.column);
    }
    let col = 0;
    while (used.has(col)) col++;

    current.push({ item, column: col, startMin, endMin });
    clusterEnd = Math.max(clusterEnd, endMin);
  }
  if (current.length > 0) clusters.push(current);

  const result: LaidOut<T>[] = [];
  for (const cluster of clusters) {
    const totalColumns = cluster.reduce((m, p) => Math.max(m, p.column), 0) + 1;
    for (const p of cluster) {
      // Tenta esticar pra direita sobre colunas livres no intervalo do card
      let colSpan = 1;
      for (let k = p.column + 1; k < totalColumns; k++) {
        const occupied = cluster.some(
          (q) =>
            q !== p &&
            q.column === k &&
            q.endMin > p.startMin &&
            q.startMin < p.endMin,
        );
        if (occupied) break;
        colSpan++;
      }
      result.push({ ...p.item, column: p.column, totalColumns, colSpan });
    }
  }
  return result;
}
